import React from 'react';

import RangeSlider from '../../RangeSlider';

import type { PricingConfiguration } from '../../../../types';

interface ICPlusSectionProps {
	configuration: PricingConfiguration;
	onUpdate: (updates: Partial<PricingConfiguration>) => void;
	handleDiscountRateChange: (
		mode: 'same_for_all' | 'different_by_card_type',
		rateType?: 'ic_plus' | 'flat_tiered',
	) => void;
	updateCardSpecificDiscountRate: (
		cardType: string,
		value: number,
		rateType?: 'ic_plus' | 'flat_tiered',
	) => void;
	updateTransactionSpecificFee: (
		transactionType: string,
		value: number,
		rateType?: 'ic_plus' | 'flat_tiered',
	) => void;
	rateType?: 'ic_plus' | 'tiered' | 'flat_tiered';
}

export const ICPlusSection: React.FC<ICPlusSectionProps> = ({
	configuration,
	onUpdate,
	handleDiscountRateChange,
	updateCardSpecificDiscountRate,
	updateTransactionSpecificFee,
	rateType = 'ic_plus',
}) => {
	// Helper functions to get rate-type-specific properties
	const getCardSpecificDiscountRates = () => {
		switch (rateType) {
			case 'ic_plus':
				return configuration.ic_plus_card_specific_discount_rates;
			case 'tiered':
				return configuration.tiered_card_specific_discount_rates;
			case 'flat_tiered':
				return configuration.flat_tiered_card_specific_discount_rates;
			default:
				return configuration.card_specific_discount_rates; // fallback
		}
	};

	const getTransactionSpecificFees = () => {
		switch (rateType) {
			case 'ic_plus':
				return configuration.ic_plus_transaction_specific_fees;
			case 'tiered':
				return configuration.tiered_transaction_specific_fees;
			case 'flat_tiered':
				return configuration.flat_tiered_transaction_specific_fees;
			default:
				return configuration.transaction_specific_fees; // fallback
		}
	};

	const getAuthorizationFeeMode = () => {
		switch (rateType) {
			case 'ic_plus':
				return configuration.ic_plus_authorization_fee_mode || 'same_for_all';
			case 'tiered':
				return configuration.tiered_authorization_fee_mode || 'same_for_all';
			case 'flat_tiered':
				return (
					configuration.flat_tiered_authorization_fee_mode || 'same_for_all'
				);
			default:
				return configuration.authorization_fee_mode || 'same_for_all'; // fallback
		}
	};

	// Get rate-type-specific discount rate mode
	const getDiscountRateMode = () => {
		switch (rateType) {
			case 'ic_plus':
				return (
					configuration.ic_plus_discount_rate_mode ||
					configuration.discount_rate_mode ||
					'same_for_all'
				);
			case 'tiered':
				return (
					configuration.tiered_discount_rate_mode ||
					configuration.discount_rate_mode ||
					'same_for_all'
				);
			case 'flat_tiered':
				return (
					configuration.flat_tiered_discount_rate_mode ||
					configuration.discount_rate_mode ||
					'same_for_all'
				);
			default:
				return configuration.discount_rate_mode || 'same_for_all'; // fallback
		}
	};

	// Get rate-type-specific standard discount rate (for "same for all" mode)
	// IMPORTANT: Each rate type should read from its OWN field only, not fall back to shared fields
	// This prevents values from one rate type bleeding into another
	const getStandardDiscountRate = () => {
		switch (rateType) {
			case 'ic_plus':
				return configuration.ic_plus_standard_discount_rate ?? 0.8;
			case 'flat_tiered':
				return configuration.flat_tiered_standard_discount_rate ?? 2.9; // FLAT_TIERED default (e.g., Stripe 2.9%)
			default:
				return configuration.standard_discount_rate ?? 0.8;
		}
	};

	// Update rate-type-specific standard discount rate and rate_config
	const updateStandardDiscountRate = (value: number) => {
		const updates: Partial<PricingConfiguration> = {};

		// Update rate-type-specific field
		switch (rateType) {
			case 'ic_plus':
				updates.ic_plus_standard_discount_rate = value;
				break;
			case 'flat_tiered':
				updates.flat_tiered_standard_discount_rate = value;
				break;
			default:
				updates.standard_discount_rate = value;
		}

		// Also update rate_config for backward compatibility
		const currentRateConfig = configuration.rate_config || {
			mode: 'same_for_all',
			standardRate: rateType === 'flat_tiered' ? 2.9 : 0.65,
			standardAuthFee: rateType === 'flat_tiered' ? 0.3 : 0.1,
			visa_mastercard_discover_credit: { discountRate: 2.9, authFee: 0.3 },
			visa_mastercard_discover_debit: { discountRate: 2.9, authFee: 0.3 },
			american_express: { discountRate: 2.9, authFee: 0.3 },
			pin_debit: { discountRate: 2.9, authFee: 0.3 },
		};

		updates.rate_config = {
			...currentRateConfig,
			standardRate: value,
		};

		onUpdate(updates);
	};

	// Get rate-type-specific standard authorization fee (for "same for all" mode)
	// IMPORTANT: Each rate type should read from its OWN field only, not fall back to shared fields
	const getStandardAuthorizationFee = () => {
		switch (rateType) {
			case 'ic_plus':
				return configuration.ic_plus_standard_authorization_fee ?? 0.1;
			case 'flat_tiered':
				return configuration.flat_tiered_standard_authorization_fee ?? 0.3; // FLAT_TIERED default (e.g., Stripe $0.30)
			default:
				return configuration.standard_authorization_fee ?? 0.1;
		}
	};

	// Update rate-type-specific standard authorization fee and rate_config
	const updateStandardAuthorizationFee = (value: number) => {
		const updates: Partial<PricingConfiguration> = {};

		// Update rate-type-specific field
		switch (rateType) {
			case 'ic_plus':
				updates.ic_plus_standard_authorization_fee = value;
				break;
			case 'flat_tiered':
				updates.flat_tiered_standard_authorization_fee = value;
				break;
			default:
				updates.standard_authorization_fee = value;
		}

		// Also update rate_config for backward compatibility
		const currentRateConfig = configuration.rate_config || {
			mode: 'same_for_all',
			standardRate: rateType === 'flat_tiered' ? 2.9 : 0.65,
			standardAuthFee: rateType === 'flat_tiered' ? 0.3 : 0.1,
			visa_mastercard_discover_credit: { discountRate: 2.9, authFee: 0.3 },
			visa_mastercard_discover_debit: { discountRate: 2.9, authFee: 0.3 },
			american_express: { discountRate: 2.9, authFee: 0.3 },
			pin_debit: { discountRate: 2.9, authFee: 0.3 },
		};

		updates.rate_config = {
			...currentRateConfig,
			standardAuthFee: value,
		};

		onUpdate(updates);
	};

	return (
		<>
			{/* Question 1: Discount Rates */}
			<div className="space-y-4 mb-6">
				<h5 className="text-lg font-medium text-gray-900">
					1. What is your desired discount rates?
				</h5>

				{/* Same discount rate for all card types */}
				<label className="flex items-start p-4 border-2 rounded-lg cursor-pointer transition-all hover:bg-gray-50 bg-white">
					<input
						type="radio"
						name={`discount_rate_mode_${rateType}`}
						value="same_for_all"
						checked={getDiscountRateMode() === 'same_for_all'}
						onChange={() =>
							handleDiscountRateChange(
								'same_for_all',
								rateType as 'ic_plus' | 'flat_tiered',
							)
						}
						className="mt-1 h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300"
					/>
					<div className="ml-4 flex-1">
						<span className="font-normal text-gray-700">
							Same discount rate for all card types
						</span>
						{getDiscountRateMode() === 'same_for_all' && (
							<div className="mt-4">
								<RangeSlider
									label="Discount Rate"
									value={getStandardDiscountRate()}
									onChange={updateStandardDiscountRate}
									min={0}
									max={5}
									step={0.01}
									unit="%"
									precision={2}
								/>
							</div>
						)}
					</div>
				</label>

				{/* Different discount rates for different card types */}
				<label className="flex items-start p-4 border-2 rounded-lg cursor-pointer transition-all hover:bg-gray-50 bg-white">
					<input
						type="radio"
						name={`discount_rate_mode_${rateType}`}
						value="different_by_card_type"
						checked={getDiscountRateMode() === 'different_by_card_type'}
						onChange={() =>
							handleDiscountRateChange(
								'different_by_card_type',
								rateType as 'ic_plus' | 'flat_tiered',
							)
						}
						className="mt-1 h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300"
					/>
					<div className="ml-4 flex-1">
						<span className="font-normal text-gray-700">
							Different discount rates for different card types
						</span>

						{getDiscountRateMode() === 'different_by_card_type' && (
							<div className="mt-4 space-y-4">
								<div className="grid grid-cols-1 gap-6">
									<RangeSlider
										label="Visa / Mastercard / Discover Credit"
										value={
											getCardSpecificDiscountRates()
												?.visa_mastercard_discover_credit ??
											(rateType === 'flat_tiered' ? 2.9 : 0.65)
										}
										onChange={(value: number) =>
											updateCardSpecificDiscountRate(
												'visa_mastercard_discover_credit',
												value,
												rateType as 'ic_plus' | 'flat_tiered',
											)
										}
										min={0}
										max={5}
										step={0.01}
										unit="%"
										precision={2}
									/>
									<RangeSlider
										label="Visa / Mastercard / Discover Debit"
										value={
											getCardSpecificDiscountRates()
												?.visa_mastercard_discover_debit ??
											(rateType === 'flat_tiered' ? 2.9 : 0.4)
										}
										onChange={(value: number) =>
											updateCardSpecificDiscountRate(
												'visa_mastercard_discover_debit',
												value,
												rateType as 'ic_plus' | 'flat_tiered',
											)
										}
										min={0}
										max={5}
										step={0.01}
										unit="%"
										precision={2}
									/>
									<RangeSlider
										label="American Express"
										value={
											getCardSpecificDiscountRates()?.american_express ??
											(rateType === 'flat_tiered' ? 3.75 : 0.7)
										}
										onChange={(value: number) =>
											updateCardSpecificDiscountRate(
												'american_express',
												value,
												rateType as 'ic_plus' | 'flat_tiered',
											)
										}
										min={0}
										max={5}
										step={0.01}
										unit="%"
										precision={2}
									/>
									<RangeSlider
										label="Pin Debit"
										value={
											getCardSpecificDiscountRates()?.pin_debit ??
											(rateType === 'flat_tiered' ? 2.9 : 0.55)
										}
										onChange={(value: number) =>
											updateCardSpecificDiscountRate(
												'pin_debit',
												value,
												rateType as 'ic_plus' | 'flat_tiered',
											)
										}
										min={0}
										max={5}
										step={0.01}
										unit="%"
										precision={2}
									/>
								</div>
							</div>
						)}
					</div>
				</label>
			</div>

			{/* Question 2: Authorization Fee */}
			<div className="space-y-4">
				<h5 className="text-lg font-medium text-gray-900">
					2. What is your Authorization Fee?
				</h5>

				{/* Same fee for all transactions */}
				<label className="flex items-start p-4 border-2 rounded-lg cursor-pointer transition-all hover:bg-gray-50 bg-white">
					<input
						type="radio"
						name={`authorization_fee_mode_${rateType}`}
						value="same_for_all"
						checked={getAuthorizationFeeMode() === 'same_for_all'}
						onChange={() => {
							const updateKey =
								`${rateType}_authorization_fee_mode` as keyof PricingConfiguration;
							onUpdate({
								authorization_fee_mode: 'same_for_all',
								[updateKey]: 'same_for_all',
							});
						}}
						className="mt-1 h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300"
					/>
					<div className="ml-4 flex-1">
						<span className="font-normal text-gray-700">
							Same fee for all transactions
						</span>
						{getAuthorizationFeeMode() === 'same_for_all' && (
							<div className="mt-4">
								<RangeSlider
									label="Authorization Fee"
									value={getStandardAuthorizationFee()}
									onChange={updateStandardAuthorizationFee}
									min={0}
									max={1}
									step={0.01}
									unit="$"
									precision={2}
								/>
							</div>
						)}
					</div>
				</label>

				{/* Different fees for different transaction types */}
				<label className="flex items-start p-4 border-2 rounded-lg cursor-pointer transition-all hover:bg-gray-50 bg-white">
					<input
						type="radio"
						name={`authorization_fee_mode_${rateType}`}
						value="different_by_transaction_type"
						checked={
							getAuthorizationFeeMode() === 'different_by_transaction_type'
						}
						onChange={() => {
							const updateKey =
								`${rateType}_authorization_fee_mode` as keyof PricingConfiguration;
							onUpdate({
								authorization_fee_mode: 'different_by_transaction_type',
								[updateKey]: 'different_by_transaction_type',
							});
						}}
						className="mt-1 h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300"
					/>
					<div className="ml-4 flex-1">
						<span className="font-normal text-gray-700">
							Different fees for different transaction types
						</span>

						{getAuthorizationFeeMode() === 'different_by_transaction_type' && (
							<div className="mt-4 space-y-4">
								<div className="grid grid-cols-1 gap-6">
									<RangeSlider
										label="Credit Transactions"
										value={
											getTransactionSpecificFees()?.credit_transactions ??
											(rateType === 'flat_tiered' ? 0.3 : 0.1)
										}
										onChange={(value: number) =>
											updateTransactionSpecificFee(
												'credit_transactions',
												value,
												rateType as 'ic_plus' | 'flat_tiered',
											)
										}
										min={0}
										max={1}
										step={0.01}
										unit="$"
										precision={2}
									/>
									<RangeSlider
										label="Debit Transactions"
										value={
											getTransactionSpecificFees()?.debit_transactions ??
											(rateType === 'flat_tiered' ? 0.3 : 0.08)
										}
										onChange={(value: number) =>
											updateTransactionSpecificFee(
												'debit_transactions',
												value,
												rateType as 'ic_plus' | 'flat_tiered',
											)
										}
										min={0}
										max={1}
										step={0.01}
										unit="$"
										precision={2}
									/>
									<RangeSlider
										label="Pin Debit Transactions"
										value={
											getTransactionSpecificFees()?.pin_debit_transactions ??
											(rateType === 'flat_tiered' ? 0.3 : 0.07)
										}
										onChange={(value: number) =>
											updateTransactionSpecificFee(
												'pin_debit_transactions',
												value,
												rateType as 'ic_plus' | 'flat_tiered',
											)
										}
										min={0}
										max={1}
										step={0.01}
										unit="$"
										precision={2}
									/>
								</div>
							</div>
						)}
					</div>
				</label>
			</div>
		</>
	);
};
